/**
 * PostImage — renders the image(s) of a feed post.
 *
 * Smart fallback logic:
 *   • If `post.image_urls` is an array of length ≥ 2 → swipe-style
 *     Instagram-like carousel with dot indicators (1/3 · 2/3 · 3/3)
 *   • Otherwise renders a single <img> from `post.image_url`
 *     (back-compatible with every pre-existing post)
 *
 * Drop-in replacement for the previous inline `<img src={post.image_url}>`
 * blocks scattered across Feed, UserProfile, ChallengeDetail,
 * PublicPost, AdminPanel.
 */
import React, { useRef, useState, useCallback } from 'react';

export default function PostImage({
  post,
  onImageClick,
  className = '',
  maxHeightClass = 'max-h-80',
  urlTransform = null,  // optional: (url) => transformedUrl (e.g. add API_URL prefix)
  'data-testid': testId = 'post-image',
}) {
  const rawUrls = Array.isArray(post?.image_urls) && post.image_urls.length > 0
    ? post.image_urls
    : (post?.image_url ? [post.image_url] : []);
  const urls = urlTransform ? rawUrls.map(urlTransform) : rawUrls;

  const [index, setIndex] = useState(0);
  const scrollRef = useRef(null);

  // Update the active dot when the user swipes.
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (!w) return;
    const next = Math.round(el.scrollLeft / w);
    if (next !== index) setIndex(Math.max(0, Math.min(urls.length - 1, next)));
  }, [index, urls.length]);

  if (urls.length === 0) return null;

  // Single image — keep the existing inline-img look.
  if (urls.length === 1) {
    return (
      <img
        src={urls[0]}
        alt="Post"
        className={`w-full rounded-xl object-cover ${maxHeightClass} cursor-pointer hover:opacity-95 transition-opacity ${className}`}
        onClick={onImageClick ? () => onImageClick(post, 0) : undefined}
        data-testid={testId}
      />
    );
  }

  // Multi-image carousel.
  return (
    <div className="relative" data-testid={`${testId}-carousel`}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={`flex overflow-x-auto snap-x snap-mandatory rounded-xl no-scrollbar ${className}`}
        style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
      >
        {urls.map((url, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-full snap-start"
            style={{ scrollSnapAlign: 'start' }}
          >
            <img
              src={url}
              alt={`Post ${i + 1}/${urls.length}`}
              className={`w-full ${maxHeightClass} object-cover cursor-pointer`}
              onClick={onImageClick ? () => onImageClick(post, i) : undefined}
              data-testid={`${testId}-slide-${i}`}
            />
          </div>
        ))}
      </div>

      {/* Counter chip (top-right) */}
      <span
        className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[11px] font-medium pointer-events-none"
        data-testid={`${testId}-counter`}
      >
        {index + 1}/{urls.length}
      </span>

      {/* Dot indicators (bottom-center) */}
      <div
        className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 pointer-events-none"
        data-testid={`${testId}-dots`}
      >
        {urls.map((_, i) => (
          <span
            key={i}
            className={`rounded-full transition-all duration-200 ${
              i === index ? 'w-2 h-2 bg-white' : 'w-1.5 h-1.5 bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
